import axios from 'axios';
import { 
  Item, 
  ItemCreateData, 
  ItemsResponse, 
  ItemFilters,
  ItemStats 
} from '@/types/item';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface GetItemsParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  active?: string;
}

class ItemService {
  async getItems(params: GetItemsParams = {}): Promise<ItemsResponse> {
    console.log('📦 ItemService: Getting items', params);
    try {
      const response = await axios.get(`${API_URL}/items`, { params });
      console.log('✅ ItemService: Items retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ ItemService: Error getting items', error.response?.data || error.message);
      throw error;
    }
  }

  async getItemById(id: number): Promise<Item> {
    console.log('📦 ItemService: Getting item by ID', { id });
    try {
      const response = await axios.get(`${API_URL}/items/${id}`);
      console.log('✅ ItemService: Item retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ ItemService: Error getting item by ID', error.response?.data || error.message);
      throw error;
    }
  }

  async createItem(itemData: ItemCreateData): Promise<Item> {
    console.log('➕ ItemService: Creating item', { name: itemData.name, type: itemData.type });
    try {
      const response = await axios.post(`${API_URL}/items`, itemData);
      console.log('✅ ItemService: Item created', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ ItemService: Error creating item', error.response?.data || error.message);
      throw error;
    }
  }

  async updateItem(id: number, itemData: Partial<ItemCreateData>): Promise<Item> {
    console.log('✏️ ItemService: Updating item', { id, name: itemData.name });
    try {
      const response = await axios.put(`${API_URL}/items/${id}`, itemData);
      console.log('✅ ItemService: Item updated', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ ItemService: Error updating item', error.response?.data || error.message);
      throw error;
    }
  }

  async deleteItem(id: number): Promise<void> {
    console.log('🗑️ ItemService: Deleting item', { id });
    try {
      const response = await axios.delete(`${API_URL}/items/${id}`);
      console.log('✅ ItemService: Item deleted', response.data);
    } catch (error: any) {
      console.log('❌ ItemService: Error deleting item', error.response?.data || error.message);
      throw error;
    }
  }

  async toggleItemStatus(id: number): Promise<Item> {
    console.log('🔄 ItemService: Toggling item status', { id });
    try {
      const response = await axios.patch(`${API_URL}/items/${id}/toggle-status`);
      console.log('✅ ItemService: Item status toggled', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ ItemService: Error toggling item status', error.response?.data || error.message);
      throw error;
    }
  }

  async searchItems(query: string, limit: number = 10): Promise<Item[]> {
    console.log('🔍 ItemService: Searching items', { query, limit });
    try {
      const response = await axios.get(`${API_URL}/items`, {
        params: { search: query, limit, active: 'true' }
      });
      console.log('✅ ItemService: Item search completed', response.data);
      return response.data.data.items;
    } catch (error: any) {
      console.log('❌ ItemService: Error searching items', error.response?.data || error.message);
      throw error;
    }
  }

  async getStats(): Promise<ItemStats> {
    console.log('📊 ItemService: Getting item statistics');
    try {
      const response = await axios.get(`${API_URL}/items/stats`);
      console.log('✅ ItemService: Item stats retrieved', response.data);
      return response.data.data;
    } catch (error: any) {
      console.log('❌ ItemService: Error getting item stats', error.response?.data || error.message);
      throw error;
    }
  }

  // Helper methods for form validation
  validateItemData(itemData: ItemCreateData): string[] {
    const errors: string[] = [];

    if (!itemData.name?.trim()) {
      errors.push('El nombre es requerido');
    }

    if (!itemData.type) {
      errors.push('El tipo de artículo es requerido');
    }

    if (itemData.price === undefined || itemData.price < 0) {
      errors.push('El precio debe ser un valor válido');
    }

    if (itemData.tax_rate === undefined || itemData.tax_rate < 0 || itemData.tax_rate > 100) {
      errors.push('La tasa de impuesto debe estar entre 0 y 100');
    }

    if (itemData.code && itemData.code.trim().length > 50) {
      errors.push('El código no puede exceder 50 caracteres');
    }

    if (itemData.description && itemData.description.trim().length > 1000) {
      errors.push('La descripción no puede exceder 1000 caracteres');
    }

    return errors;
  }

  // Format item data for display
  formatItemType(type: 'PRODUCTO' | 'SERVICIO'): string {
    return type === 'PRODUCTO' ? 'Producto' : 'Servicio';
  }

  formatItemStatus(active: boolean): string {
    return active ? 'Activo' : 'Inactivo';
  }

  formatPrice(price: number, currency: string = 'GTQ'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  }

  formatTaxRate(taxRate: number): string {
    return `${taxRate}%`;
  }

  // Filter and sort utilities
  filterItems(items: Item[], filters: ItemFilters): Item[] {
    return items.filter(item => {
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = 
          item.name.toLowerCase().includes(searchLower) ||
          item.code?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }

      if (filters.type && filters.type !== '') {
        if (item.type !== filters.type) return false;
      }

      if (filters.active !== undefined && filters.active !== '') {
        if (item.active !== filters.active) return false;
      }

      return true;
    });
  }

  sortItems(items: Item[], sortBy: keyof Item, sortOrder: 'asc' | 'desc' = 'asc'): Item[] {
    return [...items].sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];

      if (aVal === bVal) return 0;
      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;

      const result = aVal < bVal ? -1 : 1;
      return sortOrder === 'asc' ? result : -result;
    });
  }

  // Business logic helpers
  calculateTotalPrice(price: number, taxRate: number): number {
    return price + (price * taxRate / 100);
  }

  calculateTaxAmount(price: number, taxRate: number): number {
    return price * taxRate / 100;
  }

  generateDefaultCode(name: string, type: 'PRODUCTO' | 'SERVICIO'): string {
    const prefix = type === 'PRODUCTO' ? 'P' : 'S';
    const cleanName = name.replace(/[^a-zA-Z0-9]/g, '').substring(0, 8).toUpperCase();
    const timestamp = Date.now().toString().slice(-4);
    return `${prefix}-${cleanName}-${timestamp}`;
  }
}

export const itemService = new ItemService();